"use client";

import React from "react";
import { Activity, CircleCheck as CheckCircle2, Cpu, Flame, Gauge, Layers, Leaf, ShieldCheck, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: "emissions",
  title: "Redução drástica de emissões",
  description: "Atomização precisa para sistemas SNCR e SCR, assegurando cobertura uniforme da câmara e minimizando o escape de amoníaco (ammonia slip).",
  icon: Leaf,
  stat: "Até 90%",
  statLabel: "Eficiência de abatimento"
}, {
  id: "efficiency",
  title: "Poupança no consumo de reagente",
  description: "Distribuição otimizada de gotas que reduz o desperdício de ureia ou amoníaco líquido, baixando diretamente os custos operacionais contínuos.",
  icon: Gauge,
  stat: "-25%",
  statLabel: "Consumo de reagentes"
}, {
  id: "compliance",
  title: "Conformidade ambiental estrita",
  description: "Garante o cumprimento contínuo das normas europeias de emissões industriais (IED/BREF), evitando coimas e paragens não planeadas da unidade.",
  icon: ShieldCheck,
  stat: "100%",
  statLabel: "Conformidade legal BREF"
}, {
  id: "reliability",
  title: "Fiabilidade em ambiente térmico",
  description: "Lanças e bicos construídos em ligas especiais resistentes a altas temperaturas, gases corrosivos e entupimentos frequentes por cinzas.",
  icon: Flame,
  stat: "> 24/7",
  statLabel: "Operação contínua fiável"
}];
const technicalSpecs = [{
  title: "Controlo do espectro de gota (D32 / SMD)",
  description: "Evita a evaporação prematura ou o choque térmico nas paredes refratárias das caldeiras."
}, {
  title: "Injeção multizona automatizada",
  description: "Ajuste dinâmico do caudal em função da carga térmica e da concentração de NOx à saída."
}, {
  title: "Materiais nobres e ligas de alta resistência",
  description: "Disponibilidade em 316L, Hastelloy, Inconel e revestimentos cerâmicos anti-desgaste."
}, {
  title: "Purga e arrefecimento integrado",
  description: "Sistemas com ar de barreira que protegem os orifícios dos bicos contra deposição e escória."
}];
export default function Noxbenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="noxtechnicalbenefits" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-18">
          <Badge variant="outline" className="mb-4 font-mono text-xs px-3 py-1">
            <Cpu className="size-3.5 mr-1 text-secondary" />
            Engenharia de Pulverização Eurocontrol
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mb-4">
            Vantagens técnicas no controlo de NOx
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Maximize o rendimento dos sistemas DeNOx através de lanças de injeção
            e bicos de alta precisão Spraying Systems Co., dimensionados à medida
            para caldeiras, incineradores e fornos industriais.
          </p>
        </div>

        {/* 4 Key Benefit Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} variants={itemVariants} data-index={i} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md hover:border-secondary/40 transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-3">
                    <div className="size-11 rounded-lg bg-accent flex items-center justify-center text-secondary mb-3">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-between pt-0">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mb-6">
                      {item.description}
                    </CardDescription>
                    <div className="pt-4 border-t border-border/60">
                      <span className="block text-2xl font-bold text-foreground">
                        {item.stat}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {item.statLabel}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Deep Dive Engineering Banner */}
        <div className="rounded-2xl bg-muted/40 border border-border p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6 space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-secondary uppercase tracking-wider">
                <Activity className="size-4" />
                <span>Parâmetros Críticos de Controlo</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                Injeção térmica rigorosa sem impacto na operação
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Cada câmara de combustão possui um perfil de temperatura específico.
                A nossa equipa técnica calcula a trajetória das gotas através de CFD
                (Computerized Fluid Dynamics) e dimensiona as lanças para a janela ideal
                de reação (900°C - 1100°C no SNCR).
              </p>
              <div className="pt-2 flex flex-wrap gap-3">
                <Button asChild size="default">
                  <Link to="/ContactsPage">Pedir dimensionamento técnico</Link>
                </Button>
                <Button asChild variant="outline" size="default">
                  <Link to="/TechnicalSolutionsPage">Ver soluções técnicas</Link>
                </Button>
              </div>
            </div>

            {/* Checklist Box */}
            <div className="lg:col-span-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {technicalSpecs.map((spec, i) => <div key={spec.title} data-index={i} className="p-4 rounded-xl bg-card border border-border shadow-2xs flex flex-col gap-1.5">
                    <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                      <CheckCircle2 className="size-4 text-secondary shrink-0" />
                      <span>{spec.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed pl-6">
                      {spec.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Fast Assurance Bar */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Zap className="size-4 text-secondary" />
            <span>
              Representantes exclusivos da <strong>Spraying Systems Co.</strong> em Portugal desde 1993.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Layers className="size-4 text-secondary" />
            <span>Suporte técnico de engenharia e modelação computacional local.</span>
          </div>
        </div>
      </div>
    </section>;
}